import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '../api/client'
import { i18nGlobal } from '../i18n'
import { readContentLock, type ContentLock } from './access'
import type { Page, PageSection, ContentBlock } from '../types'

const t = (key: string) => i18nGlobal.t(key)

/**
 * Eén pagina uit een API-antwoord halen.
 *
 * De PageController verpakt een enkele pagina consequent in `page`
 * ({ message, page }), niet in `data` — dat laatste is de vorm van
 * JsonResource-collecties (de lijst). Wie hier `data` las kreeg undefined
 * terug: aanmaken leek te mislukken terwijl de pagina wél was aangemaakt,
 * en publiceren zette een gat in de lijst. `data` blijft als terugval staan
 * voor endpoints die ooit die vorm zouden teruggeven.
 */
function readPage(response: any): Page {
  return response.data?.page ?? response.data?.data
}

export const usePageStore = defineStore('pages', () => {
  // State
  const pages = ref<Page[]>([])
  const currentPage = ref<Page | null>(null)
  const isLoading = ref(false)
  const isSaving = ref(false)
  const error = ref<string | null>(null)
  const hasUnsavedChanges = ref(false)
  // Homepage-weergave: linkermenu verbergen op de startpagina
  const homepageHideSidebar = ref(false)
  /**
   * Gezet wanneer de API deze pagina achter een slot houdt (403). PageView
   * toont dan het ontgrendelformulier in plaats van "niet gevonden" — een
   * afgeschermde pagina bestáát wel, hij is alleen niet voor jou.
   */
  const contentLock = ref<ContentLock | null>(null)

  // Getters
  const publishedPages = computed(() => 
    pages.value.filter(p => p.status === 'published')
  )

  const draftPages = computed(() => 
    pages.value.filter(p => p.status === 'draft')
  )

  const pagesBySection = computed(() => {
    const grouped: Record<number, Page[]> = {}
    pages.value.forEach(page => {
      const sectionId = page.sectionId || 0
      if (!grouped[sectionId]) {
        grouped[sectionId] = []
      }
      grouped[sectionId].push(page)
    })
    return grouped
  })

  // Actions
  async function fetchPages(params?: { sectionId?: number; status?: string }) {
    isLoading.value = true
    error.value = null

    try {
      // Deze view heeft geen paginering: het overzicht filtert/telt client-side
      // over de volledige lijst. De backend pagineert standaard op 20; zonder
      // dit zou "aantal pagina's" (en de tabel zelf) bij >20 pagina's altijd
      // op 20 blijven steken, ook na dupliceren/aanmaken.
      const response = await api.get('/pages', { params: { ...params, per_page: 1000 } })
      pages.value = response.data.data
    } catch (err: any) {
      error.value = err.response?.data?.message || t('pages.errors.fetchListFailed')
    } finally {
      isLoading.value = false
    }
  }

  async function fetchPage(id: number) {
    isLoading.value = true
    error.value = null

    try {
      const response = await api.get(`/pages/${id}`)
      // API returns { page: {...} } for single page
      currentPage.value = response.data.page || response.data.data
      hasUnsavedChanges.value = false
      return currentPage.value
    } catch (err: any) {
      error.value = err.response?.data?.message || t('pages.errors.fetchFailed')
      throw err
    } finally {
      isLoading.value = false
    }
  }

  /** De aangewezen homepage (publiek). currentPage wordt null als er geen is. */
  async function fetchHomepage() {
    isLoading.value = true
    error.value = null
    contentLock.value = null
    try {
      const response = await api.get('/homepage')
      currentPage.value = response.data.page || null
      homepageHideSidebar.value = !!response.data.hideSidebar
      hasUnsavedChanges.value = false
      return currentPage.value
    } catch (err: any) {
      contentLock.value = readContentLock(err)
      error.value = err.response?.data?.message || t('pages.errors.homepageFailed')
      currentPage.value = null
      return null
    } finally {
      isLoading.value = false
    }
  }

  /** Publieke pagina (bezoekersweergave): geen auth, 404 voor verborgen/concept.
   *  Met tabSlug wordt de tab (kindpagina) opgehaald, incl. ouder + tabbalk. */
  async function fetchPublicPage(sectionSlug: string, pageSlug: string, tabSlug?: string) {
    isLoading.value = true
    error.value = null
    contentLock.value = null

    try {
      const url = tabSlug
        ? `/pages/${sectionSlug}/${pageSlug}/${tabSlug}`
        : `/pages/${sectionSlug}/${pageSlug}`
      const response = await api.get(url)
      currentPage.value = response.data.page
      hasUnsavedChanges.value = false
      return currentPage.value
    } catch (err: any) {
      contentLock.value = readContentLock(err)
      if (contentLock.value) currentPage.value = null
      error.value = err.response?.data?.message || t('pages.errors.notFound')
      throw err
    } finally {
      isLoading.value = false
    }
  }

  async function fetchPageBySlug(slug: string) {
    isLoading.value = true
    error.value = null

    try {
      const response = await api.get(`/pages/slug/${slug}`)
      currentPage.value = response.data.data
      hasUnsavedChanges.value = false
      return response.data.data
    } catch (err: any) {
      error.value = err.response?.data?.message || t('pages.errors.fetchFailed')
      throw err
    } finally {
      isLoading.value = false
    }
  }

  // Payload in snake_case: requests gaan onbewerkt naar de API (api/client.ts
  // vertaalt alléén antwoorden), dus de veldnamen zijn hier die van de API.
  async function createPage(data: Record<string, unknown>) {
    isSaving.value = true
    error.value = null

    try {
      const response = await api.post('/pages', data)
      const newPage = readPage(response)
      pages.value.push(newPage)
      return newPage
    } catch (err: any) {
      error.value = err.response?.data?.message || t('pages.errors.createFailed')
      throw err
    } finally {
      isSaving.value = false
    }
  }

  async function updatePage(id: number, data: Record<string, unknown>) {
    isSaving.value = true
    error.value = null

    try {
      const response = await api.put(`/pages/${id}`, data)
      const updatedPage = readPage(response)

      // Update in list
      const index = pages.value.findIndex(p => p.id === id)
      if (index !== -1) {
        pages.value[index] = updatedPage
      }
      
      // Update current page if it's the one being edited
      if (currentPage.value?.id === id) {
        currentPage.value = updatedPage
      }
      
      hasUnsavedChanges.value = false
      return updatedPage
    } catch (err: any) {
      error.value = err.response?.data?.message || t('pages.errors.updateFailed')
      throw err
    } finally {
      isSaving.value = false
    }
  }

  async function deletePage(id: number) {
    try {
      await api.delete(`/pages/${id}`)
      pages.value = pages.value.filter(p => p.id !== id)
      
      if (currentPage.value?.id === id) {
        currentPage.value = null
      }
    } catch (err: any) {
      error.value = err.response?.data?.message || t('pages.errors.deleteFailed')
      throw err
    }
  }

  async function publishPage(id: number) {
    try {
      const response = await api.post(`/pages/${id}/publish`)
      const updatedPage = readPage(response)

      const index = pages.value.findIndex(p => p.id === id)
      if (index !== -1) {
        pages.value[index] = updatedPage
      }
      
      if (currentPage.value?.id === id) {
        currentPage.value = updatedPage
      }
      
      return updatedPage
    } catch (err: any) {
      error.value = err.response?.data?.message || t('pages.errors.publishFailed')
      throw err
    }
  }

  async function unpublishPage(id: number) {
    try {
      const response = await api.post(`/pages/${id}/unpublish`)
      const updatedPage = readPage(response)

      const index = pages.value.findIndex(p => p.id === id)
      if (index !== -1) {
        pages.value[index] = updatedPage
      }
      
      if (currentPage.value?.id === id) {
        currentPage.value = updatedPage
      }
      
      return updatedPage
    } catch (err: any) {
      error.value = err.response?.data?.message || t('pages.errors.unpublishFailed')
      throw err
    }
  }

  async function duplicatePage(id: number) {
    try {
      const response = await api.post(`/pages/${id}/duplicate`)
      const newPage = readPage(response)
      pages.value.push(newPage)
      return newPage
    } catch (err: any) {
      error.value = err.response?.data?.message || t('pages.errors.duplicateFailed')
      throw err
    }
  }

  // Local state management for editing
  function markAsChanged() {
    hasUnsavedChanges.value = true
  }

  function clearCurrentPage() {
    currentPage.value = null
    hasUnsavedChanges.value = false
  }

  return {
    // State
    pages,
    currentPage,
    isLoading,
    isSaving,
    error,
    hasUnsavedChanges,
    homepageHideSidebar,
    contentLock,

    // Getters
    publishedPages,
    draftPages,
    pagesBySection,

    // Actions
    fetchPages,
    fetchPage,
    fetchHomepage,
    fetchPublicPage,
    fetchPageBySlug,
    createPage,
    updatePage,
    deletePage,
    publishPage,
    unpublishPage,
    duplicatePage,
    markAsChanged,
    clearCurrentPage,
  }
})
